import { AdditionalWeeksReason, DashDonationType, WageAndTravelReimbursementRequestStatus } from '@nkr/enums/index';

export type AdditionalWeekDocumentType = 'complicationsReport';

export interface IAdditionalWeekPolicyRule {
    isSupported: boolean;
    autoApproved: boolean;
    requiresReasonForCenterApproval: boolean;
    allowedReasons: AdditionalWeeksReason[];
    complicationsRequireReport: boolean;
}

export const getAdditionalWeekPolicyRule = (organType: DashDonationType, week: number): IAdditionalWeekPolicyRule => {
    if (week <= 0) {
        return {
            isSupported: false,
            autoApproved: false,
            requiresReasonForCenterApproval: false,
            allowedReasons: [],
            complicationsRequireReport: false
        };
    }

    if (organType === DashDonationType.Kidney) {
        if (week <= 3) {
            return { isSupported: true, autoApproved: true, requiresReasonForCenterApproval: false, allowedReasons: [], complicationsRequireReport: false };
        }

        if (week === 4) {
            return { isSupported: true, autoApproved: false, requiresReasonForCenterApproval: false, allowedReasons: [], complicationsRequireReport: false };
        }

        if (week === 5 || week === 6) {
            return {
                isSupported: true,
                autoApproved: false,
                requiresReasonForCenterApproval: true,
                allowedReasons: [AdditionalWeeksReason.ManualLabor, AdditionalWeeksReason.Complications],
                complicationsRequireReport: true
            };
        }
        return {
            isSupported: true,
            autoApproved: false,
            requiresReasonForCenterApproval: true,
            allowedReasons: [AdditionalWeeksReason.Complications],
            complicationsRequireReport: true
        };
    }

    if (organType === DashDonationType.Liver) {
        if (week <= 6) {
            return { isSupported: true, autoApproved: true, requiresReasonForCenterApproval: false, allowedReasons: [], complicationsRequireReport: false };
        }

        if (week === 7 || week === 8) {
            return { isSupported: true, autoApproved: false, requiresReasonForCenterApproval: false, allowedReasons: [], complicationsRequireReport: false };
        }

        if (week === 9 || week === 10) {
            return {
                isSupported: true,
                autoApproved: false,
                requiresReasonForCenterApproval: true,
                allowedReasons: [AdditionalWeeksReason.ManualLabor, AdditionalWeeksReason.Complications],
                complicationsRequireReport: true
            };
        }

        if (week === 11 || week === 12) {
            return {
                isSupported: true,
                autoApproved: false,
                requiresReasonForCenterApproval: true,
                allowedReasons: [AdditionalWeeksReason.Complications],
                complicationsRequireReport: true
            };
        }
    }

    return {
        isSupported: false,
        autoApproved: false,
        requiresReasonForCenterApproval: false,
        allowedReasons: [],
        complicationsRequireReport: false
    };
};

export const isReasonAllowedForCenterApproval = (organType: DashDonationType, week: number, reason?: number): boolean => {
    const rule = getAdditionalWeekPolicyRule(organType, week);
    if (!rule.requiresReasonForCenterApproval) {
        return true;
    }
    return reason != null && rule.allowedReasons.includes(reason as AdditionalWeeksReason);
};

export const requiresPhysicianInitialsForCenterApproval = (
    organType: DashDonationType,
    week: number,
    reason?: number
): boolean => {
    const rule = getAdditionalWeekPolicyRule(organType, week);
    if (reason === AdditionalWeeksReason.Complications && rule.complicationsRequireReport) {
        return true;
    }

    if (
        reason === AdditionalWeeksReason.ManualLabor
        && rule.requiresReasonForCenterApproval
        && rule.allowedReasons.includes(AdditionalWeeksReason.ManualLabor)
        && rule.allowedReasons.includes(AdditionalWeeksReason.Complications)
    ) {
        return true;
    }

    return false;
};

export const getRequiredDocumentsForCenterApproval = (organType: DashDonationType, week: number, reason?: number): AdditionalWeekDocumentType[] => {
    const rule = getAdditionalWeekPolicyRule(organType, week);
    if (reason !== AdditionalWeeksReason.Complications) {
        return [];
    }

    const docs: AdditionalWeekDocumentType[] = [];
    if (rule.complicationsRequireReport) {
        docs.push('complicationsReport');
    }

    return docs;
};

/** Earliest week number in the set that requires a complications report when reason is Complications (single shared upload anchor). */
export const getAnchorWeekForSharedComplicationsReport = (
    organType: DashDonationType,
    weeks: number[],
    reason?: number
): number | null => {
    if (reason !== AdditionalWeeksReason.Complications || !weeks?.length) {
        return null;
    }

    const needing = weeks.filter((w) => getRequiredDocumentsForCenterApproval(organType, w, reason).includes('complicationsReport'));
    if (needing.length === 0) {
        return null;
    }

    return Math.min(...needing);
};

export const needsPolicyDocuments = (
    status: WageAndTravelReimbursementRequestStatus,
    organType: DashDonationType,
    week: number,
    reason?: number
): boolean => {
    
    if (status !== WageAndTravelReimbursementRequestStatus.CenterApproved) {
        return false;
    }

    return getRequiredDocumentsForCenterApproval(organType, week, reason).length > 0;
};
